import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Calculator, Plus, Pencil, Trash2, GitCompare, FileText, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMULA_TYPES = [
  { value: "hourly", label: "Hourly (with OT)" },
  { value: "salary", label: "Salary" },
  { value: "commission", label: "Commission (quote/invoice)" },
  { value: "piecework", label: "Piecework (custom pieces)" },
  { value: "tiered_hourly", label: "Tiered Hourly" },
  { value: "custom", label: "Custom expression" },
];

const PROVINCES = ["ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE", "YT", "NT", "NU"];

const blankFormula = () => ({
  name: "", formula_type: "hourly", config: { base_rate: 25, ot_threshold_hours: 40, ot_multiplier: 1.5 },
  pull_hours_from_schedule: true, apply_cpp: true, apply_ei: true,
  apply_federal_tax: true, apply_provincial_tax: true, province_code: "ON", is_active: true, notes: "",
});

const blankInputs = () => ({
  hours_worked: 0, weekend_hours: 0, holiday_hours: 0, tips: 0,
  pieces: {}, quotes_total: 0, invoices_paid_total: 0, pay_periods_per_year: 26,
});

function ConfigEditor({ formulaType, config, onChange }) {
  const set = (patch) => onChange({ ...config, ...patch });

  if (formulaType === "hourly") return (
    <div className="grid grid-cols-2 gap-3">
      <div><Label>Base rate ($/h)</Label><Input type="number" step="0.01" value={config.base_rate || 0} onChange={(e) => set({ base_rate: parseFloat(e.target.value || 0) })} data-testid="cfg-base-rate" /></div>
      <div><Label>OT threshold (hours)</Label><Input type="number" value={config.ot_threshold_hours || 40} onChange={(e) => set({ ot_threshold_hours: parseFloat(e.target.value || 40) })} /></div>
      <div><Label>OT multiplier</Label><Input type="number" step="0.1" value={config.ot_multiplier || 1.5} onChange={(e) => set({ ot_multiplier: parseFloat(e.target.value || 1.5) })} /></div>
      <div><Label>Weekend multiplier</Label><Input type="number" step="0.1" value={config.weekend_multiplier || 1} onChange={(e) => set({ weekend_multiplier: parseFloat(e.target.value || 1) })} /></div>
      <div><Label>Holiday multiplier</Label><Input type="number" step="0.1" value={config.holiday_multiplier || 1.5} onChange={(e) => set({ holiday_multiplier: parseFloat(e.target.value || 1.5) })} /></div>
    </div>
  );

  if (formulaType === "salary") return (
    <div className="grid grid-cols-2 gap-3">
      <div><Label>Annual salary ($)</Label><Input type="number" value={config.annual_salary || 0} onChange={(e) => set({ annual_salary: parseFloat(e.target.value || 0) })} data-testid="cfg-annual-salary" /></div>
      <div><Label>Pay periods/year</Label><Input type="number" value={config.pay_periods_per_year || 26} onChange={(e) => set({ pay_periods_per_year: parseInt(e.target.value || 26) })} /></div>
    </div>
  );

  if (formulaType === "commission") return (
    <div className="space-y-3">
      <div>
        <Label>Commission source</Label>
        <Select value={config.source || "quote"} onValueChange={(v) => set({ source: v })}>
          <SelectTrigger data-testid="cfg-commission-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="quote">Quote sale value</SelectItem>
            <SelectItem value="invoice">Invoice paid value</SelectItem>
            <SelectItem value="both">Both (quote + invoice)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Base rate ($/h)</Label><Input type="number" step="0.01" value={config.base_rate || 0} onChange={(e) => set({ base_rate: parseFloat(e.target.value || 0) })} /></div>
        <div><Label>Quote %</Label><Input type="number" step="0.1" value={config.quote_pct || 0} onChange={(e) => set({ quote_pct: parseFloat(e.target.value || 0) })} /></div>
        <div><Label>Invoice %</Label><Input type="number" step="0.1" value={config.invoice_pct || 0} onChange={(e) => set({ invoice_pct: parseFloat(e.target.value || 0) })} /></div>
      </div>
    </div>
  );

  if (formulaType === "piecework") {
    const pieces = config.pieces || [];
    const updatePiece = (i, k, v) => {
      const next = [...pieces];
      next[i] = { ...next[i], [k]: v };
      set({ pieces: next });
    };
    return (
      <div className="space-y-3">
        <div className="space-y-2">
          {pieces.map((p, i) => (
            <div key={i} className="flex gap-2 items-end" data-testid={`piece-row-${i}`}>
              <div className="flex-1"><Label>Piece label</Label><Input value={p.label || ""} onChange={(e) => updatePiece(i, "label", e.target.value)} /></div>
              <div className="w-32"><Label>Rate ($)</Label><Input type="number" step="0.01" value={p.rate || 0} onChange={(e) => updatePiece(i, "rate", parseFloat(e.target.value || 0))} /></div>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => set({ pieces: pieces.filter((_, j) => j !== i) })}>×</Button>
            </div>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={() => set({ pieces: [...pieces, { label: "", rate: 0 }] })} data-testid="add-piece-btn"><Plus className="h-3 w-3 mr-1" />Add piece type</Button>
        <div className="grid grid-cols-1">
          <Label>Hourly floor ($/h) — optional safety net</Label>
          <Input type="number" step="0.01" value={config.hourly_floor || 0} onChange={(e) => set({ hourly_floor: parseFloat(e.target.value || 0) })} />
          <p className="text-xs text-muted-foreground mt-1">If hourly_floor × hours_worked &gt; piece total, the floor pays instead.</p>
        </div>
      </div>
    );
  }

  if (formulaType === "tiered_hourly") {
    const tiers = config.tiers || [];
    const upd = (i, k, v) => { const n = [...tiers]; n[i] = { ...n[i], [k]: v }; set({ tiers: n }); };
    return (
      <div className="space-y-3">
        {tiers.map((t, i) => (
          <div key={i} className="flex gap-2 items-end">
            <div className="flex-1"><Label>Up to (hours)</Label><Input type="number" value={t.up_to_hours || 0} onChange={(e) => upd(i, "up_to_hours", parseFloat(e.target.value || 0))} /></div>
            <div className="w-32"><Label>Rate ($/h)</Label><Input type="number" step="0.01" value={t.rate || 0} onChange={(e) => upd(i, "rate", parseFloat(e.target.value || 0))} /></div>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => set({ tiers: tiers.filter((_, j) => j !== i) })}>×</Button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => set({ tiers: [...tiers, { up_to_hours: 0, rate: 0 }] })}><Plus className="h-3 w-3 mr-1" />Add tier</Button>
        <div><Label>Default rate (beyond tiers)</Label><Input type="number" step="0.01" value={config.default_rate || 0} onChange={(e) => set({ default_rate: parseFloat(e.target.value || 0) })} /></div>
      </div>
    );
  }

  if (formulaType === "custom") return (
    <div>
      <Label>Expression</Label>
      <Textarea value={config.formula || ""} onChange={(e) => set({ formula: e.target.value })} placeholder="hours_worked * 22 + tips" rows={3} className="font-mono" />
      <p className="text-xs text-muted-foreground mt-1">Vars: hours_worked, weekend_hours, holiday_hours, tips, quotes_total, invoices_paid_total. Only numbers + + - * / ( ) allowed.</p>
    </div>
  );

  return null;
}

export default function PayEngine() {
  const [tab, setTab] = useState("formulas");
  const [formulas, setFormulas] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  // Simulator
  const [simFormula, setSimFormula] = useState(null);
  const [simInputs, setSimInputs] = useState(blankInputs());
  const [simResult, setSimResult] = useState(null);
  // Compare
  const [compareB, setCompareB] = useState(null);
  const [compareResult, setCompareResult] = useState(null);

  const load = async () => {
    try { const r = await axios.get(`${API}/pay-formulas?include_inactive=true`); setFormulas(r.data || []); }
    catch { toast.error("Failed to load formulas"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit?.name) { toast.error("Name required"); return; }
    try {
      if (edit.id) await axios.put(`${API}/pay-formulas/${edit.id}`, edit);
      else await axios.post(`${API}/pay-formulas`, edit);
      toast.success("Saved"); setOpen(false); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate?")) return;
    try { await axios.delete(`${API}/pay-formulas/${id}`); load(); }
    catch { toast.error("Failed"); }
  };

  const runSim = async () => {
    if (!simFormula) { toast.error("Pick a formula or define inline"); return; }
    try {
      const body = simFormula.id
        ? { formula_id: simFormula.id, inputs: simInputs }
        : { formula_type: simFormula.formula_type, config: simFormula.config, province_code: simFormula.province_code,
            apply_cpp: simFormula.apply_cpp, apply_ei: simFormula.apply_ei,
            apply_federal_tax: simFormula.apply_federal_tax, apply_provincial_tax: simFormula.apply_provincial_tax,
            inputs: simInputs };
      const r = await axios.post(`${API}/pay-formulas/simulate`, body);
      setSimResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulate failed"); }
  };

  const runCompare = async () => {
    if (!simFormula || !compareB) { toast.error("Pick two formulas to compare"); return; }
    try {
      const mk = (f) => f.id
        ? { formula_id: f.id, inputs: simInputs }
        : { formula_type: f.formula_type, config: f.config, province_code: f.province_code, inputs: simInputs };
      const r = await axios.post(`${API}/pay-formulas/compare`, { a: mk(simFormula), b: mk(compareB) });
      setCompareResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Compare failed"); }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="pay-engine-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><Calculator className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pay Engine</h1>
          <p className="text-muted-foreground">Build custom pay formulas. Simulate paystubs before applying.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="formulas" data-testid="tab-formulas"><FileText className="h-4 w-4 mr-1" /> Formulas</TabsTrigger>
          <TabsTrigger value="simulate" data-testid="tab-simulate"><Sparkles className="h-4 w-4 mr-1" /> Simulator</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare"><GitCompare className="h-4 w-4 mr-1" /> Compare</TabsTrigger>
        </TabsList>

        {/* FORMULAS */}
        <TabsContent value="formulas">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div><CardTitle>{formulas.length} formulas</CardTitle><CardDescription>Tenant default if no employee assigned. Otherwise per-employee.</CardDescription></div>
              <Button onClick={() => { setEdit(blankFormula()); setOpen(true); }} data-testid="new-formula-btn"><Plus className="h-4 w-4 mr-1" /> New formula</Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Employee</TableHead><TableHead>Province</TableHead><TableHead>Active</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {formulas.map((f) => (
                    <TableRow key={f.id} className={!f.is_active ? "opacity-50" : ""}>
                      <TableCell className="font-medium">{f.name}</TableCell>
                      <TableCell><Badge variant="outline">{FORMULA_TYPES.find((t) => t.value === f.formula_type)?.label || f.formula_type}</Badge></TableCell>
                      <TableCell className="text-xs">{f.employee_name || <span className="text-muted-foreground">Tenant default</span>}</TableCell>
                      <TableCell>{f.province_code}</TableCell>
                      <TableCell>{f.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEdit({ ...f }); setOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { setSimFormula({ ...f }); setTab("simulate"); }} data-testid={`sim-formula-${f.id}`}><Sparkles className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(f.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {formulas.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No formulas yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SIMULATOR */}
        <TabsContent value="simulate">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Inputs</CardTitle><CardDescription>Sample data — actual pay runs pull these from your time clock + calendar.</CardDescription></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Formula</Label>
                  <Select value={simFormula?.id || ""} onValueChange={(v) => setSimFormula(formulas.find((f) => f.id === v))}>
                    <SelectTrigger data-testid="sim-pick-formula"><SelectValue placeholder="Pick a saved formula" /></SelectTrigger>
                    <SelectContent>{formulas.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Hours worked</Label><Input type="number" step="0.25" value={simInputs.hours_worked} onChange={(e) => setSimInputs({ ...simInputs, hours_worked: parseFloat(e.target.value || 0) })} data-testid="sim-hours" /></div>
                  <div><Label>Weekend hours</Label><Input type="number" step="0.25" value={simInputs.weekend_hours} onChange={(e) => setSimInputs({ ...simInputs, weekend_hours: parseFloat(e.target.value || 0) })} /></div>
                  <div><Label>Holiday hours</Label><Input type="number" step="0.25" value={simInputs.holiday_hours} onChange={(e) => setSimInputs({ ...simInputs, holiday_hours: parseFloat(e.target.value || 0) })} /></div>
                  <div><Label>Tips</Label><Input type="number" step="0.01" value={simInputs.tips} onChange={(e) => setSimInputs({ ...simInputs, tips: parseFloat(e.target.value || 0) })} /></div>
                  <div><Label>Quotes total ($)</Label><Input type="number" step="0.01" value={simInputs.quotes_total} onChange={(e) => setSimInputs({ ...simInputs, quotes_total: parseFloat(e.target.value || 0) })} /></div>
                  <div><Label>Invoices paid ($)</Label><Input type="number" step="0.01" value={simInputs.invoices_paid_total} onChange={(e) => setSimInputs({ ...simInputs, invoices_paid_total: parseFloat(e.target.value || 0) })} /></div>
                  <div><Label>Pay periods/yr</Label><Input type="number" value={simInputs.pay_periods_per_year} onChange={(e) => setSimInputs({ ...simInputs, pay_periods_per_year: parseInt(e.target.value || 26) })} /></div>
                </div>
                {simFormula?.formula_type === "piecework" && simFormula.config?.pieces && (
                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-sm font-medium">Piece quantities</p>
                    {simFormula.config.pieces.map((p, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Label className="w-1/2">{p.label}</Label>
                        <Input type="number" value={simInputs.pieces[p.label] || 0} onChange={(e) => setSimInputs({ ...simInputs, pieces: { ...simInputs.pieces, [p.label]: parseFloat(e.target.value || 0) } })} />
                      </div>
                    ))}
                  </div>
                )}
                <Button onClick={runSim} className="w-full" data-testid="run-simulate-btn"><Sparkles className="h-4 w-4 mr-1" /> Simulate paystub</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Sample paystub</CardTitle><CardDescription>{simResult?.disclaimer || "Run the simulator to preview."}</CardDescription></CardHeader>
              <CardContent>
                {!simResult ? <p className="text-muted-foreground">No result yet.</p> : (
                  <div className="space-y-3" data-testid="sim-result">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="border rounded p-3"><p className="text-xs text-muted-foreground">Gross</p><p className="text-2xl font-bold text-emerald-700">${simResult.gross.toFixed(2)}</p></div>
                      <div className="border rounded p-3"><p className="text-xs text-muted-foreground">Deductions</p><p className="text-2xl font-bold text-amber-700">${simResult.deductions.total.toFixed(2)}</p></div>
                      <div className="border rounded p-3 bg-primary/5"><p className="text-xs text-muted-foreground">Net</p><p className="text-2xl font-bold">${simResult.net.toFixed(2)}</p></div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">Gross breakdown</p>
                      <table className="w-full text-sm">
                        <tbody>
                          {simResult.gross_breakdown.map((b, i) => (
                            <tr key={i} className="border-b"><td className="py-1">{b.label}</td><td className="text-right">${b.amount.toFixed(2)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">Deductions</p>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b"><td>CPP</td><td className="text-right">${simResult.deductions.cpp.toFixed(2)}</td></tr>
                          <tr className="border-b"><td>EI</td><td className="text-right">${simResult.deductions.ei.toFixed(2)}</td></tr>
                          <tr className="border-b"><td>Federal tax</td><td className="text-right">${simResult.deductions.federal_tax.toFixed(2)}</td></tr>
                          <tr className="border-b"><td>Provincial tax</td><td className="text-right">${simResult.deductions.provincial_tax.toFixed(2)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* COMPARE */}
        <TabsContent value="compare">
          <Card>
            <CardHeader><CardTitle>Compare two formulas</CardTitle><CardDescription>Side-by-side delta. Uses the same Inputs as the Simulator tab.</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Formula A</Label>
                  <Select value={simFormula?.id || ""} onValueChange={(v) => setSimFormula(formulas.find((f) => f.id === v))}>
                    <SelectTrigger data-testid="compare-a"><SelectValue placeholder="Pick A" /></SelectTrigger>
                    <SelectContent>{formulas.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Formula B</Label>
                  <Select value={compareB?.id || ""} onValueChange={(v) => setCompareB(formulas.find((f) => f.id === v))}>
                    <SelectTrigger data-testid="compare-b"><SelectValue placeholder="Pick B" /></SelectTrigger>
                    <SelectContent>{formulas.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={runCompare} data-testid="run-compare-btn"><GitCompare className="h-4 w-4 mr-1" /> Compare</Button>
              {compareResult && (
                <div className="grid grid-cols-3 gap-3 mt-3 text-center" data-testid="compare-result">
                  <div className="border rounded p-3"><p className="text-xs text-muted-foreground">A · Net</p><p className="text-xl font-bold">${compareResult.a.net.toFixed(2)}</p></div>
                  <div className="border rounded p-3"><p className="text-xs text-muted-foreground">Δ Net</p><p className={`text-xl font-bold ${compareResult.delta.net >= 0 ? "text-emerald-700" : "text-destructive"}`}>${compareResult.delta.net.toFixed(2)}</p></div>
                  <div className="border rounded p-3"><p className="text-xs text-muted-foreground">B · Net</p><p className="text-xl font-bold">${compareResult.b.net.toFixed(2)}</p></div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Formula editor */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEdit(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit formula" : "New formula"}</DialogTitle><DialogDescription>All pay formulas can pull hours straight from the calendar/schedule.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-3 max-h-[70vh] overflow-auto">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="formula-name" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={edit.formula_type} onValueChange={(v) => setEdit({ ...edit, formula_type: v, config: {} })}>
                    <SelectTrigger data-testid="formula-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMULA_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="rounded-lg border p-3"><ConfigEditor formulaType={edit.formula_type} config={edit.config} onChange={(c) => setEdit({ ...edit, config: c })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Province</Label>
                  <Select value={edit.province_code} onValueChange={(v) => setEdit({ ...edit, province_code: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer pt-6">
                  <Switch checked={edit.pull_hours_from_schedule} onCheckedChange={(v) => setEdit({ ...edit, pull_hours_from_schedule: v })} data-testid="pull-hours-switch" />
                  <span>Pull hours from calendar/schedule</span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={edit.apply_cpp} onCheckedChange={(v) => setEdit({ ...edit, apply_cpp: v })} /> Apply CPP</label>
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={edit.apply_ei} onCheckedChange={(v) => setEdit({ ...edit, apply_ei: v })} /> Apply EI</label>
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={edit.apply_federal_tax} onCheckedChange={(v) => setEdit({ ...edit, apply_federal_tax: v })} /> Federal tax</label>
                <label className="flex items-center gap-2 cursor-pointer"><Switch checked={edit.apply_provincial_tax} onCheckedChange={(v) => setEdit({ ...edit, apply_provincial_tax: v })} /> Provincial tax</label>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-formula-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
